import React, { useEffect, useReducer, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import Close from '../icons/Close'
import Radom, {
  InputField, InputFieldDataType, IPaymentLink, IProduct,
  IProductUpdate
} from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { Currencies } from '../util/Currencies'
import { SecondsToPaymentPeriod, errorToast, successToast } from '../util/Util'
import { IconButton, PrimaryButton, HoverText, IconLinkButton } from './Button'
import Switch from '../components/Switch'
import ProductForm, { ProductType } from './ProductForm'
import Dropdown from './Dropdown'
import { InputLabel, InputWithCheckbox, TextInput, TextInputWithLabel, UnstyledInput } from './Input'
import Modal from './Modal'
import Tooltip from './Tooltip'
import Checkbox from './Checkbox'
import { cloneDeep, isEqual } from 'lodash'
import { ManagedPaymentMethodSelector } from './ManagedMethodMultiselector'
import { ManagedPaymentMethod, getMethod } from '../util/Managed'
import ColorPicker from 'react-best-gradient-color-picker'
import { IProductCreateV2 } from '../views/Products/CreateProduct'
import SearchMultiselect from './SearchMultiselect'
import {
  DesignerDescription,
  DesignerFooter,
  DesignerFormBody,
  DesignerFormStack,
  DesignerHeader,
  DesignerHeaderCopy,
  DesignerHeaderIcon,
  DesignerKicker,
  DesignerPanel,
  DesignerSection,
  DesignerSectionDescription,
  DesignerSectionHeader,
  DesignerSectionTitle,
  DesignerSetting,
  DesignerSettingDescription,
  DesignerSettingText,
  DesignerSettingTitle,
  DesignerTitle,
  DesignerInlineGrid
} from './DesignerFormLayout'

const InputPresets = [
  { label: 'Collect contact’s name', name: 'Name' },
  { label: 'Collect contact’s email', name: 'Email address' }
]

const ChainLogo = styled.img`
  filter: grayscale(1);
  width: auto;
  height: 14px;
  transition: 0.2s ease all;
  opacity: 0.5;
`

const DropdownItem = styled.div`
  padding: 10px;
  cursor: pointer;

  :hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};

    ${ChainLogo} {
      opacity: 1;
      filter: grayscale(0);
    }
  }
`

const InputPresetList = styled.div`
  display: grid;
  gap: 12px;
  align-items: center;
`

const InputPreset = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  width: fit-content;
  transition: 0.2s ease all;

  &:hover {
    color: ${RADOM_COLORS.BLACK};
  }

  &:active {
    opacity: 0.5;
  }
`

const CustomInputList = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) 20px;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
`

const emptyInputField = {
  dataType: InputFieldDataType.String,
  inputLabel: '',
  isRequired: true
}

interface IProps {
  edit?: boolean
  isLoading?: boolean
  isSubmitting?: boolean
  paymentLink: IPaymentLink
  skipRemoteData?: boolean
  onFormSubmit: () => void
  onFormChange: (form: Partial<IPaymentLink>) => void
}

const PaymentLinkForm = (props: IProps): React.ReactElement => {
  const [searchParams] = useSearchParams()

  const [enableRedirect, setEnableRedirect] = useState(false)
  const [productModalOpen, setProductModalOpen] = useState(false)
  const [inputFieldCloseFns, setInputFieldCloseFns] = useState<any>({})
  const [customInputs, setCustomInputs] = useState<InputField[]>([])
  const [products, setProducts] = useState([] as IProduct[])
  const [selectedMethods, setSelectedMethods] = useState<ManagedPaymentMethod[]>(
    props.paymentLink.gateway.managed?.methods.map(m => getMethod(m.network, m.token)) ?? []
  )

  const emptyProduct: any = {
    name: '',
    description: '',
    chargingIntervalSeconds: 0,
    currency: Currencies[0].ticker,
    price: '',
    image: undefined,
    addOns: []
  }

  useEffect(() => {
    if (!props.paymentLink.gateway.managed) {
      return
    }

    props.onFormChange({
      gateway: {
        managed: {
          methods: selectedMethods.map(m => ({
            network: m.hostChain?.name ?? m.name,
            token: m.hostChain?.tokenAddress ?? undefined
          }))
        }
      }
    })
  }, [selectedMethods])

  const [productForm, updateProductForm] = useReducer((p: IProductCreateV2, n: Partial<IProductCreateV2>) => {
    return { ...p, ...n }
  }, emptyProduct)

  const handleUpdateProductForm = (form: IProductUpdate): void => {
    updateProductForm(form)
  }

  const loadProducts = async (): Promise<void> => {
    if (props.skipRemoteData) {
      const seededProducts = props.paymentLink.products.filter(p => !p.productType?.Presale)
      setProducts(seededProducts)

      if (searchParams.has('product_id')) {
        const selectedProduct = seededProducts.find(p => p.id === searchParams.get('product_id'))
        if (selectedProduct) {
          props.onFormChange({ products: [selectedProduct] })
        }
      }

      return
    }

    Radom.getProducts()
      .then(products => {
        setProducts(products.data.filter(p => !p.productType?.Presale))
        return products
      })
      .then((products) => {
        if (searchParams.has('product_id')) {
          const selectedProduct = products.data.find(p => p.id === searchParams.get('product_id'))
          if (selectedProduct) {
            props.onFormChange({ products: [selectedProduct] })
          }
        }
      })
      .catch(err => errorToast(err.response?.data?.error || err.message))
  }

  const onCreateProduct = async (): Promise<void> => {
    try {
      const newProduct = await Radom.createProduct({
        ...productForm,
        chargingIntervalSeconds: productForm.chargingIntervalSeconds !== undefined &&
         productForm.chargingIntervalSeconds <= 0
          ? undefined
          : productForm.chargingIntervalSeconds,
        image: undefined
      } as any)

      if (productForm.image) {
        const formData = new FormData()
        formData.append('entityId', newProduct.id)
        formData.append('imageType', 'productImage')
        formData.append('image', productForm.image)
        await Radom.uploadImage(formData)
      }

      const product = await Radom.getProduct(newProduct.id)

      setProductModalOpen(false)
      successToast('Created product!')

      loadProducts()
        .then(() => {
          props.onFormChange({ products: [...props.paymentLink.products, product] })
        })
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const onFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    props.onFormSubmit()
  }

  useEffect(() => {
    setCustomInputs(props.paymentLink.inputFields.filter(i => !InputPresets.some(ii => ii.name === i.inputLabel)))
  }, [])

  useEffect(() => {
    if ((props.paymentLink.cancelUrl && props.paymentLink.cancelUrl.trim().length > 0) ||
      (props.paymentLink.successUrl && props.paymentLink.successUrl.trim().length > 0)) {
      setEnableRedirect(true)
    } else {
      setEnableRedirect(false)
    }
  }, [props.paymentLink.cancelUrl, props.paymentLink.successUrl])

  useEffect(() => {
    const _inputPresets = props.paymentLink.inputFields
      .filter(i => InputPresets.map(i => i.name).includes(i.inputLabel))
    const inputFields = [..._inputPresets, ...customInputs]

    if (isEqual(props.paymentLink.inputFields, inputFields)) {
      return
    }

    props.onFormChange({ inputFields: cloneDeep(inputFields) })
  }, [customInputs])

  useEffect(() => {
    loadProducts()
  }, [])

  useEffect(() => {
    if (!props.skipRemoteData) {
      return
    }

    setProducts(props.paymentLink.products.filter(p => !p.productType?.Presale))
  }, [props.skipRemoteData, props.paymentLink.products])

  return <>
    <DesignerPanel>
      <DesignerHeader>
        <DesignerHeaderIcon>
          <IconLinkButton to='/payment_links' type="button" style={{ padding: 0, display: 'flex' }}>
            <Close style={{ width: 14, pointerEvents: 'none' }} />
          </IconLinkButton>
        </DesignerHeaderIcon>
        <DesignerHeaderCopy>
          <DesignerKicker>Payment links</DesignerKicker>
          <DesignerTitle>{props.edit ? 'Edit payment link' : 'Create payment link'}</DesignerTitle>
          <DesignerDescription>
            Configure the contact-facing payment page and preview the live checkout flow.
          </DesignerDescription>
        </DesignerHeaderCopy>
      </DesignerHeader>
      <form onSubmit={onFormSubmit} style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0
      }}>
        <DesignerFormBody>
          <DesignerFormStack>
            <DesignerSection>
              <DesignerSectionHeader>
                <DesignerSectionTitle>
                  Payment methods
                  <Tooltip message='Choose the cryptocurrencies and networks contacts can use on this payment link.' />
                </DesignerSectionTitle>
                <DesignerSectionDescription>
                  Choose the accepted payment methods and what this link is collecting.
                </DesignerSectionDescription>
              </DesignerSectionHeader>
              {
                props.paymentLink.gateway.managed &&
                <ManagedPaymentMethodSelector
            groupByAsset
                  hideLabel
                  allowTestnets
                  selectedMethods={props.paymentLink.gateway.managed.methods.map(m => getMethod(m.network, m.token))}
                  setSelectedMethods={setSelectedMethods} />
              }
              <div
                style={{
                  background: 'var(--surface, #ffffff)',
                  borderRadius: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  width: '100%'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <InputLabel>Products</InputLabel>
                    <Tooltip message='Products set the amount shown on the hosted payment page.' />
                  </div>

                  <SearchMultiselect
                    disabled={props.paymentLink.customizations.allowFreeInputAmount}
                    itemArray={products}
                    selectedItems={props.paymentLink.products}
                    setSelectedItems={ products => props.onFormChange({ products })}
                    keyExtractor={p => p.name + p.id}
                    headers={[
                      <DropdownItem
                        key='create'
                        style={{ display: 'flex', alignItems: 'center', padding: 15, width: '100%' }}
                        onClick={() => setProductModalOpen(true) }>
                        <span style={{ color: RADOM_COLORS.BLACK }}>+ Create new product</span>
                      </DropdownItem>
                    ]}
                    dropdownListItemStyle={{
                      borderBottom: `1px solid ${RADOM_COLORS.GRAY_MED}`
                    }}
                    labelExtractor={p =>
                      <DropdownItem style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: 3,
                        maxWidth: 400
                      }}>
                        {p.imageUrl && <img src={p.imageUrl} style={{ width: 20, height: 'auto' }} /> }
                        <div style={{ display: 'flex', flexDirection: 'column' }}>

                          <span style={{ overflowWrap: 'anywhere' }}>{p.name}</span>
                          <div style={{ display: 'flex' }}>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: 12, opacity: 0.6 }}>
                              {Currencies.find(c => c.ticker.toLowerCase() === p.currency.toLowerCase())?.icon}
                              {new Intl.NumberFormat('en-US', { maximumFractionDigits: 12 }).format(p.price)}
                              {
                                p.chargingIntervalSeconds !== undefined && p.chargingIntervalSeconds > 0 &&
                            ` per ${SecondsToPaymentPeriod(p.chargingIntervalSeconds).toLowerCase()}`
                              }
                            </div>
                          </div>
                        </div>
                      </DropdownItem>}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <InputLabel>Allow custom payment amount</InputLabel>
                    <Tooltip message='Let contacts enter their own amount. This is disabled while products are selected.' />

                  </div>
                  <Switch
                    checked={props.paymentLink.customizations.allowFreeInputAmount}
                    disabled={props.paymentLink.products.length > 0}
                    onClick={() => {
                      props.onFormChange({
                        customizations: {
                          ...props.paymentLink.customizations,
                          allowFreeInputAmount: !props.paymentLink.customizations.allowFreeInputAmount
                        }
                      })
                    }}
                  />
                </div>
              </div>
            </DesignerSection>

            <DesignerSection>
              <DesignerSectionHeader>
                <DesignerSectionTitle>Contact experience</DesignerSectionTitle>
                <DesignerSectionDescription>
                  Decide what contacts see after choosing a currency and which details are collected before payment.
                </DesignerSectionDescription>
              </DesignerSectionHeader>
              <div>
                <DesignerSetting style={{ marginBottom: 10 }}>
                  <DesignerSettingText>
                    <DesignerSettingTitle>
                    Network fee
                      <Tooltip message='If this is enabled, the network fee will be added to the checkout total and paid by the contact.' />
                    </DesignerSettingTitle>
                    <DesignerSettingDescription>
                    Pass network costs through when the payment is created.
                    </DesignerSettingDescription>
                  </DesignerSettingText>
                </DesignerSetting>
                <InputPresetList>
                  <InputPreset
                    onClick={() => props.onFormChange({
                      chargeCustomerNetworkFee: !props.paymentLink.chargeCustomerNetworkFee
                    })}>
                    <Checkbox
                      size={16}
                      checked={!!props.paymentLink.chargeCustomerNetworkFee} />
                    <span>Pass network fee charge to contact</span>
                  </InputPreset>
                </InputPresetList>
              </div>

              <div>
                <DesignerSetting style={{ marginBottom: 10 }}>
                  <DesignerSettingText>
                    <DesignerSettingTitle>Email receipt</DesignerSettingTitle>
                    <DesignerSettingDescription>
                    Collect an email address and send a receipt after payment.
                    </DesignerSettingDescription>
                  </DesignerSettingText>
                </DesignerSetting>
                <InputPresetList>
                  <InputPreset
                    onClick={() => {
                      if (!props.paymentLink.sendEmailReceipt && !props.paymentLink.inputFields.map(i => i.inputLabel).includes('Email address')) {
                        props.onFormChange({
                          inputFields: [...props.paymentLink.inputFields, { dataType: InputFieldDataType.Email, inputLabel: 'Email address', isRequired: true }]
                        })
                      }
                      props.onFormChange({ sendEmailReceipt: !props.paymentLink.sendEmailReceipt })
                    } }>
                    <Checkbox
                      size={16}
                      checked={!!props.paymentLink.sendEmailReceipt} />
                    <span>Send contact an email receipt</span>
                  </InputPreset>
                </InputPresetList>
              </div>

              <div>
                <DesignerSetting style={{ marginBottom: 10 }}>
                  <DesignerSettingText>
                    <DesignerSettingTitle>
                    Input fields
                      <Tooltip message='Create inputs to collect contact data.' />
                    </DesignerSettingTitle>
                    <DesignerSettingDescription>
                    These fields are hidden in the preview so you can focus on the payment flow.
                    </DesignerSettingDescription>
                  </DesignerSettingText>
                </DesignerSetting>
                <InputPresetList>
                  {
                    InputPresets.map((input, index) =>
                      <InputPreset
                        key={index}
                        onClick={() => {
                          const newInputs = props.paymentLink.inputFields.slice()
                          if (props.paymentLink.inputFields.map(i => i.inputLabel).includes(input.name)) {
                          // Remove preset input from inputFields
                            props.onFormChange({
                              inputFields: newInputs.filter(i => i.inputLabel !== input.name)
                            })
                          } else {
                          // Add preset input to inputFields
                            const presetInput: InputField = {
                              ...emptyInputField,
                              inputLabel: input.name,
                              dataType: input.name === 'Email address' ? InputFieldDataType.Email : InputFieldDataType.String
                            }
                            props.onFormChange({
                              inputFields: [...newInputs, presetInput]
                            })
                          }
                        }}>
                        <Checkbox
                          size={16}
                          checked={props.paymentLink.inputFields.map(i => i.inputLabel).includes(input.name)} />
                        <span>{input.label}</span>
                      </InputPreset>
                    )
                  }
                  <InputPreset
                    onClick={() => {
                      const checked = customInputs.length > 0
                      if (checked) {
                        setCustomInputs([])
                      } else {
                        setCustomInputs([{ ...emptyInputField }])
                      }
                    }}>
                    <Checkbox size={16} checked={customInputs.length > 0} />
                    <span>Add custom fields</span>
                  </InputPreset>
                </InputPresetList>
                {
                  customInputs.length > 0 &&
                  <CustomInputList>
                    {
                      customInputs
                        .map((field, fieldIndex) => [
                          <Dropdown
                            key={`${fieldIndex}-dataType`}
                            onCloseFn={fn => {
                              const newInputFieldFns = cloneDeep(inputFieldCloseFns)
                              newInputFieldFns[fieldIndex] = fn
                              setInputFieldCloseFns(newInputFieldFns)
                            }}
                            selectedContent={
                              <div style={{ fontSize: 14, minWidth: 'max-content' }}>
                                { InputFieldDataType[field.dataType] }
                              </div>
                            }
                            dropdownContent={
                              Object.entries(InputFieldDataType)
                                .filter(c => field.dataType !== c[0])
                                .map((c, i) =>
                                  <DropdownItem
                                    key={i}
                                    style={{ fontSize: 14 }}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const newInputs = customInputs.slice()
                                      newInputs[fieldIndex].dataType = c[0] as InputFieldDataType
                                      inputFieldCloseFns[fieldIndex]?.()
                                      setCustomInputs(newInputs)
                                    }}
                                  >
                                    {c[1]}
                                  </DropdownItem>
                                )
                            } />,
                          <InputWithCheckbox key={`${fieldIndex}-input`}>
                            <UnstyledInput
                              type="text"
                              value={field.inputLabel}
                              placeholder="Input label"
                              onChange={(e) => {
                                const val = (e.target as HTMLInputElement).value
                                const newInputs = cloneDeep(customInputs.slice())
                                newInputs[fieldIndex].inputLabel = val
                                setCustomInputs(newInputs)
                              }} />
                            <div style={{
                              display: 'flex',
                              gap: 4,
                              color: RADOM_COLORS.GRAY_DARKER,
                              alignItems: 'center',
                              borderLeft: `1px solid ${RADOM_COLORS.GRAY_MED}`,
                              paddingLeft: '8px',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                            onClick={() => {
                              const newInputs = customInputs.slice()
                              newInputs[fieldIndex].isRequired = !newInputs[fieldIndex].isRequired
                              setCustomInputs(newInputs)
                            }}>
                              <span style={{ fontSize: 12 }}>Optional</span>
                              <Checkbox size={15} checked={!field.isRequired} />
                            </div>
                          </InputWithCheckbox>,
                          <div style={{ justifySelf: 'end' }} key={`${fieldIndex}-close`}>
                            <IconButton type="button" style={{ padding: 5 }} onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              const newInputs = customInputs.slice()
                              newInputs.splice(fieldIndex, 1)
                              setCustomInputs(newInputs)
                            }}>
                              <Close style={{ width: 12, pointerEvents: 'none' }} />
                            </IconButton>
                          </div>
                        ])
                    }
                  </CustomInputList>
                }
                {
                  customInputs.length > 0 &&
                  customInputs.length < 2 &&
                  <HoverText
                    style={{ padding: 10, width: 'fit-content' }}
                    onClick={() => {
                      const newInputs = customInputs.slice()
                      newInputs.push({ ...emptyInputField })
                      setCustomInputs(newInputs)
                    }}>
                      + Add input field
                  </HoverText>
                }
              </div>

            </DesignerSection>

            <DesignerSection>
              <DesignerSectionHeader>
                <DesignerSectionTitle>
                  Branding and behavior
                  <Tooltip message='Customize the hosted page colors and optional post-payment redirects.' />
                </DesignerSectionTitle>
                <DesignerSectionDescription>
                  Tune the visual treatment and optional redirect behavior for the hosted page.
                </DesignerSectionDescription>
              </DesignerSectionHeader>
              <DesignerInlineGrid>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <InputLabel>Panel color</InputLabel>
                  <div style={{ width: 'fit-content' }}>
                    <Dropdown
                      overflow='initial'
                      innerStyle={{ maxHeight: '100vh' }}
                      selectedContent={
                        <div
                          style={{
                            width: 25,
                            height: 25,
                            borderRadius: 5,
                            background: props.paymentLink.customizations.leftPanelColor
                          }}
                        />
                      }
                      dropdownContent={
                        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <ColorPicker
                            value={props.paymentLink.customizations.leftPanelColor}
                            onChange={c => {
                              props.onFormChange({
                                customizations: { ...props.paymentLink.customizations, leftPanelColor: c }
                              })
                            }}
                          />
                          <TextInput
                            value={props.paymentLink.customizations.leftPanelColor}
                            onChange={v => props.onFormChange({
                              customizations: { ...props.paymentLink.customizations, leftPanelColor: v.target.value }
                            })}
                          />
                        </div>
                      }
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <InputLabel>Button color</InputLabel>
                  <div style={{ width: 'fit-content' }}>
                    <Dropdown
                      overflow='initial'
                      innerStyle={{ maxHeight: '100vh' }}
                      selectedContent={
                        <div style={{
                          width: 25,
                          height: 25,
                          borderRadius: 5,
                          background: props.paymentLink.customizations.primaryButtonColor
                        }} />
                      }
                      dropdownContent={
                        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <ColorPicker
                            value={props.paymentLink.customizations.primaryButtonColor}
                            onChange={c => {
                              props.onFormChange({
                                customizations: { ...props.paymentLink.customizations, primaryButtonColor: c }
                              })
                            }}
                          />
                          <TextInput
                            value={props.paymentLink.customizations.primaryButtonColor}
                            onChange={v => props.onFormChange({
                              customizations: {
                                ...props.paymentLink.customizations,
                                primaryButtonColor: v.target.value
                              }
                            })}
                          />
                        </div>
                      }
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <InputLabel>Slanted edge</InputLabel>
                  <Switch
                    checked={props.paymentLink.customizations.slantedEdge}
                    onClick={() => {
                      props.onFormChange({
                        customizations: {
                          ...props.paymentLink.customizations,
                          slantedEdge: !props.paymentLink.customizations.slantedEdge
                        }
                      })
                    }}
                  />
                </div>
              </DesignerInlineGrid>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InputLabel>Allow discount codes</InputLabel>
                <Switch
                  checked={props.paymentLink.customizations.allowDiscountCodes}
                  onClick={() => {
                    props.onFormChange({
                      customizations: {
                        ...props.paymentLink.customizations,
                        allowDiscountCodes: !props.paymentLink.customizations.allowDiscountCodes
                      }
                    })
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InputLabel>Enable redirect URLs</InputLabel>
                <Switch
                  checked={enableRedirect}
                  onClick={() => setEnableRedirect(!enableRedirect)}
                />
                {enableRedirect && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <TextInputWithLabel
                        label="Success URL"
                        placeholder="https://"
                        value={props.paymentLink.successUrl}
                        onChange={(e) => props.onFormChange({ successUrl: (e.target as HTMLInputElement).value })
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <TextInputWithLabel
                        label="Cancel URL"
                        placeholder="https://"
                        value={props.paymentLink.cancelUrl}
                        onChange={(e) => props.onFormChange({ cancelUrl: (e.target as HTMLInputElement).value })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </DesignerSection>
          </DesignerFormStack>
        </DesignerFormBody>
        <DesignerFooter>
          <PrimaryButton style={{
            width: '100%',
            gap: 6
          }} isLoading={props.isSubmitting}>
            {props.edit
              ? 'Save changes'
              : <>
                <span style={{ fontSize: 12.5, lineHeight: 1, fontWeight: 500 }}>+</span>
                <span>Create payment link</span>
              </>
            }
          </PrimaryButton>
        </DesignerFooter>
      </form>
    </DesignerPanel>
    <Modal
      visible={productModalOpen}
      onClose={() => setProductModalOpen(false)}
      title='Create product'>
      <ProductForm
        modal
        form={productForm}
        allowedProductTypes={[ProductType.OneTime, ProductType.Subscription]}
        onFormSubmit={onCreateProduct}
        onFormUpdate={handleUpdateProductForm}
        isSubmitting={props.isSubmitting ?? false}
        isLoading={props.isLoading ?? false}
      />
    </Modal>
  </>
}

export default PaymentLinkForm
